import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertCircle, CalendarClock, CheckCircle2, Download, History, Loader2, Plus, Sparkles, Trash2, Undo2, Wallet } from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/**
 * InstallmentPlanCard — pago aplazado UI for the "Cursos del usuario" modal.
 *
 * Two modes:
 *   · No plan → shows the "Activar pago aplazado" toggle. Admin picks the
 *     monthly quota (100/200/300 €) and the courses to bundle; the preview
 *     endpoint computes the plazos. On save, `_create_plan_for_user` runs
 *     server-side, auto-assigns courses and schedules plazos.
 *   · Active plan → renders the plazos table with a checkbox per row that
 *     any manager can flip to "paid" (which auto-creates a ticket for that
 *     plazo). Superadmin can also promote each plazo's ticket to a full
 *     invoice and cancel the plan.
 */
export default function InstallmentPlanCard({ user, courses, onPlanChanged }) {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const isManagerBypass =
    currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const canCreatePlan =
    isManagerBypass || !!(currentUser?.perms || {}).create_installment_plans;
  const [plan, setPlan] = useState(undefined); // undefined = loading, null = no plan, object = plan
  const [creating, setCreating] = useState(false);
  // When the previous plan is completed/cancelled the SA/admin can start a
  // new one on top of the historical view. This toggle reveals the form.
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);

  const reloadPlan = async () => {
    try {
      const { data } = await api.get(`/admin/users/${user.id}/installment-plan`);
      setPlan(data);
    } catch (err) {
      if (err.response?.status === 404) setPlan(null);
      else toast.error("No se pudo cargar el plan de pago");
    }
  };

  useEffect(() => {
    if (user?.id) reloadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (plan === undefined) {
    return <div className="p-3 text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Cargando plan…</div>;
  }

  const planIsClosed = plan && (plan.status === "completed" || plan.status === "cancelled");

  return (
    <Card className="p-4 border border-amber-200 bg-amber-50/40 rounded-lg space-y-3" data-testid="installment-plan-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-900">
          <Wallet className="w-4 h-4" />
          <p className="text-sm font-semibold">Pago aplazado</p>
        </div>
        {plan?.is_blocked && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300">
            <AlertCircle className="w-3 h-3" /> Bloqueado por impago
          </span>
        )}
        {plan?.status === "completed" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
            <CheckCircle2 className="w-3 h-3" /> Plan completado
          </span>
        )}
        {plan?.status === "cancelled" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 border border-gray-300">
            <Trash2 className="w-3 h-3" /> Plan cancelado
          </span>
        )}
      </div>

      {!plan ? (
        canCreatePlan ? (
          <CreatePlanForm
            user={user}
            courses={courses}
            creating={creating}
            setCreating={setCreating}
            onCreated={async () => { await reloadPlan(); onPlanChanged?.(); }}
          />
        ) : (
          <p
            className="text-xs text-amber-900/80 bg-white border border-amber-200 rounded-md p-3"
            data-testid="installment-plan-no-perm"
          >
            Tu rol no tiene el permiso para crear planes de pago aplazado.
            Pide al superadmin que active <span className="font-medium">
            «Crear pago aplazado en alumno»</span> en tus permisos.
          </p>
        )
      ) : (
        <>
          <ActivePlanView
            plan={plan}
            isSuperadmin={isSuperadmin}
            onChanged={async () => { await reloadPlan(); onPlanChanged?.(); }}
          />
          {planIsClosed && canCreatePlan && (
            <div className="pt-3 border-t border-amber-200/70 space-y-3">
              {!showNewPlanForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewPlanForm(true)}
                  data-testid="installment-plan-new"
                  className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Crear un nuevo plan de pago aplazado
                </Button>
              ) : (
                <div className="rounded-md bg-white border border-amber-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                      Nuevo plan de pago aplazado
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewPlanForm(false)}
                      className="text-gray-500 hover:text-gray-700 h-6 px-2 text-[11px]"
                    >
                      Cancelar
                    </Button>
                  </div>
                  <CreatePlanForm
                    user={user}
                    courses={courses}
                    creating={creating}
                    setCreating={setCreating}
                    onCreated={async () => {
                      setShowNewPlanForm(false);
                      await reloadPlan();
                      onPlanChanged?.();
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ------------------ Create plan form ------------------ */
function CreatePlanForm({ user, courses, creating, setCreating, onCreated }) {
  const [enabled, setEnabled] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [monthly, setMonthly] = useState(200);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [allTests, setAllTests] = useState(null); // fetched lazily when the toggle opens

  useEffect(() => {
    if (!enabled || allTests !== null) return;
    api
      .get("/admin/courses")
      .then((r) => setAllTests(r.data || []))
      .catch(() => setAllTests([]));
  }, [enabled, allTests]);

  // Pool of buyable courses: full course catalog with a numeric price. We
  // fall back to the assignments-only ``courses`` prop while the catalog loads.
  const catalog = (allTests || []).map((t) => ({
    course_id: t.id,
    title: t.title,
    price: t.price,
    kind: t.kind,
  }));
  const pool = catalog.length ? catalog : (courses || []);
  const eligible = pool.filter((c) => c.price != null && Number(c.price) > 0);

  const toggleCourse = (cid) => {
    setSelectedCourses((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));
  };

  useEffect(() => {
    if (!enabled || selectedCourses.length === 0) {
      setPreview(null);
      setPreviewErr("");
      return;
    }
    let cancel = false;
    setPreviewErr("");
    api
      .post(`/admin/installments/preview`, {
        monthly_amount: Number(monthly),
        course_ids: selectedCourses,
      })
      .then((r) => { if (!cancel) setPreview(r.data); })
      .catch((e) => { if (!cancel) { setPreview(null); setPreviewErr(e.response?.data?.detail || "Error"); } });
    return () => { cancel = true; };
  }, [enabled, monthly, selectedCourses]);

  const submit = async () => {
    if (selectedCourses.length === 0) {
      toast.error("Selecciona al menos un curso");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/admin/users/${user.id}/installment-plan`, {
        monthly_amount: Number(monthly),
        course_ids: selectedCourses,
      });
      toast.success("Plan de pago aplazado creado");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el plan");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <label className="flex items-start gap-3 p-3 rounded-md bg-white border border-amber-200 cursor-pointer">
        <Checkbox
          checked={enabled}
          onCheckedChange={(v) => setEnabled(!!v)}
          className="mt-0.5"
          data-testid="plan-toggle"
        />
        <div>
          <p className="text-sm font-medium text-amber-900">Activar pago aplazado</p>
          <p className="text-[11px] text-amber-800/80 mt-0.5">
            Divide el total de los cursos seleccionados en plazos mensuales. Cada plazo pagado genera un ticket automático.
          </p>
        </div>
      </label>
      {enabled && (
        <div className="space-y-3 pt-2">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-1">Cuota mensual</p>
            <RadioGroup value={String(monthly)} onValueChange={(v) => setMonthly(v)} className="flex gap-3" data-testid="plan-monthly-group">
              {[100, 200, 300].map((amt) => (
                <label key={amt} className="flex items-center gap-2 px-3 py-2 rounded-md border bg-white cursor-pointer hover:bg-amber-50">
                  <RadioGroupItem value={String(amt)} data-testid={`plan-monthly-${amt}`} />
                  <span className="text-sm font-mono-data">{amt} €/mes</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-1">
              Cursos incluidos en el plan
              {eligible.length === 0 && <span className="text-red-600 ml-2 font-normal normal-case">Ningún curso con precio configurado — añade precio primero.</span>}
            </p>
            <div className="max-h-40 overflow-y-auto space-y-1 border rounded-md bg-white p-2" data-testid="plan-course-list">
              {eligible.map((c) => (
                <label key={c.course_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-amber-50 cursor-pointer">
                  <Checkbox
                    checked={selectedCourses.includes(c.course_id)}
                    onCheckedChange={() => toggleCourse(c.course_id)}
                    data-testid={`plan-course-${c.course_id}`}
                  />
                  <span className="text-sm flex-1 truncate" title={c.title}>{c.title}</span>
                  <span className="text-xs font-mono-data text-amber-900">{fmtEur(c.price)}</span>
                </label>
              ))}
            </div>
          </div>

          {preview && (
            <div className="bg-white rounded-md border border-amber-200 p-3" data-testid="plan-preview">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-amber-900 font-semibold uppercase">
                  Previsualización · {preview.installments.length} plazos
                </p>
                <p className="text-sm font-mono-data text-amber-900">Total {fmtEur(preview.total_amount)}</p>
              </div>
              <ul className="space-y-0.5">
                {preview.installments.map((i) => (
                  <li key={i.n} className="flex justify-between text-xs font-mono-data text-gray-700">
                    <span>Plazo {i.n} · vence {fmtDate(i.due_date)}</span>
                    <span>{fmtEur(i.amount_eur)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {previewErr && <p className="text-xs text-red-600">{previewErr}</p>}

          <Button
            onClick={submit}
            disabled={creating || selectedCourses.length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="plan-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
            Crear plan de {selectedCourses.length} curso{selectedCourses.length === 1 ? "" : "s"}
          </Button>
        </div>
      )}
    </>
  );
}

/* ------------------ Active plan view ------------------ */
function ActivePlanView({ plan, isSuperadmin, onChanged }) {
  const { user: currentUser } = useAuth();
  const isManagerBypass =
    currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const canExtendPlan =
    isManagerBypass || !!(currentUser?.perms || {}).create_installment_plans;
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelPayment, setConfirmCancelPayment] = useState(null); // installment being cancelled, or null
  const [promoteTx, setPromoteTx] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showExtend, setShowExtend] = useState(false);

  const markPaid = async (n) => {
    if (busy) return;
    setBusy(`pay-${n}`);
    try {
      await api.post(`/admin/installments/${plan.id}/pay/${n}`);
      toast.success(`Plazo ${n} marcado como pagado (ticket generado)`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al marcar el plazo");
    } finally {
      setBusy(null);
    }
  };

  const downloadPlazoTicket = async (txId, ticketNumber) => {
    try {
      await downloadBlob(
        `${API}/admin/invoices/${txId}/pdf`,
        `ticket-${ticketNumber || txId}.pdf`,
      );
    } catch (e) {
      toast.error(e.message);
    }
  };

  const cancelPayment = async (n) => {
    setBusy(`cancel-pay-${n}`);
    try {
      await api.post(`/admin/installments/${plan.id}/pay/${n}/cancel`);
      toast.success(`Pago del plazo ${n} cancelado — vuelve a pendiente`);
      setConfirmCancelPayment(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar el pago");
    } finally {
      setBusy(null);
    }
  };

  const cancelPlan = async () => {
    setBusy("cancel");
    try {
      await api.delete(`/admin/installments/${plan.id}`);
      toast.success("Plan cancelado");
      setConfirmCancel(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setBusy(null);
    }
  };

  const paidCount = plan.installments.filter((i) => i.status === "paid").length;
  const totalPaid = plan.installments
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + Number(i.amount_eur || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-amber-900">
        <span>Cuota mensual: <strong className="font-mono-data">{fmtEur(plan.monthly_amount)}</strong></span>
        <span>Total: <strong className="font-mono-data">{fmtEur(plan.total_amount)}</strong></span>
        <span>Pagado: <strong className="font-mono-data">{fmtEur(totalPaid)} ({paidCount}/{plan.installments.length})</strong></span>
        <span>Creado: {fmtDate(plan.created_at)}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white border border-amber-200 rounded" data-testid="plan-installments-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-600 border-b bg-amber-50">
              <th className="text-left py-2 px-2 w-10">#</th>
              <th className="text-left py-2 px-2">Vence</th>
              <th className="text-right py-2 px-2">Importe</th>
              <th className="text-left py-2 px-2">Estado</th>
              <th className="text-left py-2 px-2">Nº ticket</th>
              <th className="text-right py-2 px-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plan.installments.map((i) => (
              <tr key={i.n} className="border-b border-amber-100" data-testid={`plan-installment-${i.n}`}>
                <td className="py-2 px-2 font-mono-data">{i.n}</td>
                <td className="py-2 px-2 text-gray-700">{fmtDate(i.due_date)}</td>
                <td className="py-2 px-2 text-right font-mono-data">{fmtEur(i.amount_eur)}</td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {i.status === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`plan-status-paid-${i.n}`}>
                        <CheckCircle2 className="w-3 h-3" /> Pagado
                      </span>
                    ) : i.status === "overdue" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-300">
                        <AlertCircle className="w-3 h-3" /> Vencido
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                        Pendiente
                      </span>
                    )}
                    {i.cancellations?.length > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500"
                        title={i.cancellations
                          .map((c) => `Cancelado por ${c.by_email || c.by_id} el ${fmtDate(c.at)} (ticket ${c.previous_ticket_number || "—"})`)
                          .join("\n")}
                        data-testid={`plan-cancellations-${i.n}`}
                      >
                        <History className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-2 font-mono-data text-brand-ink text-xs">{i.ticket_number || "—"}</td>
                <td className="py-2 px-2 text-right space-x-1">
                  {i.status !== "paid" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markPaid(i.n)}
                      disabled={busy === `pay-${i.n}`}
                      data-testid={`plan-pay-${i.n}`}
                      className="border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
                    >
                      {busy === `pay-${i.n}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Marcar pagado"}
                    </Button>
                  ) : (
                    <>
                      {i.tx_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadPlazoTicket(i.tx_id, i.ticket_number)}
                          data-testid={`plan-download-${i.n}`}
                          title="Descargar ticket"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isSuperadmin && i.tx_id && !i.invoice_number && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPromoteTx(i)}
                          data-testid={`plan-invoice-${i.n}`}
                          title="Generar factura"
                          className="text-purple-700 hover:bg-purple-50"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {i.invoice_number && (
                        <span className="text-[10px] font-mono-data text-purple-700 px-1">
                          {i.invoice_number}
                        </span>
                      )}
                      {i.invoice_number ? (
                        <span
                          className="inline-block text-gray-300"
                          title="Ya tiene factura emitida — la cancelación se gestiona desde facturación"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmCancelPayment(i.n)}
                          data-testid={`plan-cancel-payment-${i.n}`}
                          title="Cancelar este pago (marcado por error)"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canExtendPlan && plan.status === "active" && (
        <div className="pt-2 border-t border-amber-200/60">
          {!showExtend ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExtend(true)}
              data-testid="plan-extend-open"
              className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Ampliar plan
            </Button>
          ) : (
            <ExtendPlanPanel
              plan={plan}
              onClose={() => setShowExtend(false)}
              onDone={() => { setShowExtend(false); onChanged(); }}
            />
          )}
        </div>
      )}

      {isSuperadmin && plan.status === "active" && (
        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmCancel(true)}
            className="text-red-600 hover:bg-red-50"
            data-testid="plan-cancel"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancelar plan
          </Button>
        </div>
      )}

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar plan de pago aplazado?</DialogTitle>
            <DialogDescription>
              El alumno mantendrá el acceso a los cursos (las asignaciones no se borran). Los tickets ya emitidos permanecen en el histórico. Solo se puede cancelar; para reactivar hay que crear un nuevo plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)}>Volver</Button>
            <Button
              onClick={cancelPlan}
              disabled={busy === "cancel"}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="plan-cancel-confirm"
            >
              Sí, cancelar plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancelPayment != null} onOpenChange={(o) => !o && setConfirmCancelPayment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar el pago del plazo {confirmCancelPayment}?</DialogTitle>
            <DialogDescription>
              El plazo vuelve a quedar pendiente y se podrá marcar como pagado de nuevo más tarde. El ticket ya
              emitido se anula (queda marcado como cancelado, no se borra) y quedará registrado quién canceló
              este pago y cuándo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmCancelPayment(null)}>Volver</Button>
            <Button
              onClick={() => cancelPayment(confirmCancelPayment)}
              disabled={busy === `cancel-pay-${confirmCancelPayment}`}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="plan-cancel-payment-confirm"
            >
              Sí, cancelar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromoteInstallmentInvoice
        plazo={promoteTx}
        plan={plan}
        onClose={() => setPromoteTx(null)}
        onDone={() => { setPromoteTx(null); onChanged(); }}
      />
    </div>
  );
}

function PromoteInstallmentInvoice({ plazo, plan, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", tax_id: "",
    address: "", postal_code: "", city: "", country: "ES",
  });

  useEffect(() => {
    if (plazo) {
      // Prefill from user (would need the user object; keep empty and let SA fill)
      setForm({
        first_name: "", last_name: "", email: "", tax_id: "",
        address: "", postal_code: "", city: "", country: "ES",
      });
    }
  }, [plazo]);

  if (!plazo) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/tx/${plazo.tx_id}/generate-invoice`,
        `factura.pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      const invNo = res.headers.get("X-Invoice-Number");
      toast.success(`Factura ${invNo || ""} emitida`);
      // Update plazo.invoice_number locally-ish via reload.
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!plazo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="plan-promote-modal">
        <DialogHeader>
          <DialogTitle>Emitir factura del plazo {plazo.n}</DialogTitle>
          <DialogDescription>
            Se generará una factura correlativa (AÑO-Fnnnn) vinculada al ticket {plazo.ticket_number}. Rellena los datos del cliente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">Nombre</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="plan-invoice-firstname" />
            </div>
            <div>
              <label className="text-xs">Apellidos / Razón social</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="plan-invoice-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">CIF/NIF/NIE</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <label className="text-xs">Email</label>
              <input className="w-full border rounded px-2 py-1 text-sm" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs">Dirección</label>
            <input className="w-full border rounded px-2 py-1 text-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs">C.P.</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="text-xs">Ciudad</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-purple-700 hover:bg-purple-800 text-white"
            data-testid="plan-invoice-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Emitir factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------ Extend plan panel (mode = extend | increase) ------------------ */
function ExtendPlanPanel({ plan, onClose, onDone }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState([]);
  const [mode, setMode] = useState("extend_installments");
  const [delta, setDelta] = useState(50);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch catalog once. Filter out courses already in the plan and untried
  // ones (price required).
  useEffect(() => {
    api
      .get("/admin/courses")
      .then((r) => setCatalog(r.data || []))
      .catch(() => setCatalog([]));
  }, []);

  const inPlan = new Set((plan.courses_snapshot || []).map((c) => c.course_id));
  const eligible = (catalog || []).filter(
    (t) => !inPlan.has(t.id) && t.price != null && Number(t.price) > 0,
  );

  const toggle = (cid) =>
    setSelected((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));

  // Live preview when the user changes courses / mode / delta.
  useEffect(() => {
    if (selected.length === 0) { setPreview(null); setPreviewErr(""); return; }
    if (mode === "increase_monthly" && (!delta || Number(delta) <= 0)) {
      setPreview(null); setPreviewErr("Indica cuántos €/mes añadir."); return;
    }
    let cancel = false;
    setPreviewErr("");
    api
      .post(`/admin/installments/${plan.id}/preview-extend`, {
        new_course_ids: selected,
        mode,
        new_monthly_delta: mode === "increase_monthly" ? Number(delta) : null,
      })
      .then((r) => { if (!cancel) setPreview(r.data); })
      .catch((e) => {
        if (!cancel) {
          setPreview(null);
          setPreviewErr(e.response?.data?.detail || "No se pudo calcular la previsualización");
        }
      });
    return () => { cancel = true; };
  }, [plan.id, selected, mode, delta]);

  const submit = async () => {
    if (selected.length === 0) { toast.error("Selecciona al menos un curso"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/installments/${plan.id}/extend`, {
        new_course_ids: selected,
        mode,
        new_monthly_delta: mode === "increase_monthly" ? Number(delta) : null,
      });
      toast.success("Plan ampliado correctamente");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo ampliar el plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 rounded-md bg-white border border-amber-300 space-y-3" data-testid="plan-extend-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Ampliar plan
        </p>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="plan-extend-close">
          Cerrar
        </Button>
      </div>

      {catalog === null ? (
        <p className="text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Cargando catálogo…</p>
      ) : eligible.length === 0 ? (
        <p className="text-xs text-gray-500">No hay cursos disponibles para añadir. Todos los cursos con precio ya están incluidos.</p>
      ) : (
        <>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Cursos a añadir</p>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
              {eligible.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 text-sm hover:bg-amber-50 rounded px-1 py-0.5 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.includes(c.id)}
                    onCheckedChange={() => toggle(c.id)}
                    data-testid={`plan-extend-course-${c.id}`}
                  />
                  <span className="flex-1">{c.title}</span>
                  <span className="text-xs text-gray-500 font-mono-data">{fmtEur(c.price)}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Cómo quieres cobrarlos</p>
            <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
              <label className="flex items-start gap-2 p-2 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="extend_installments" id="mode-extend" data-testid="plan-extend-mode-extend" />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Mantener la cuota actual · alargar plazos</p>
                  <p className="text-xs text-gray-500">
                    Cuota mensual = {fmtEur(plan.monthly_amount)}. Se añaden más plazos al final por el importe extra.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="increase_monthly" id="mode-increase" data-testid="plan-extend-mode-increase" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-brand-ink">Aumentar la cuota mensual</p>
                  <p className="text-xs text-gray-500 mb-1.5">
                    Se suma un extra a la cuota actual y se recalculan los plazos pendientes.
                  </p>
                  {mode === "increase_monthly" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Cuota actual {fmtEur(plan.monthly_amount)} +</span>
                      <input
                        type="number"
                        min="1"
                        step="10"
                        className="w-24 border rounded px-2 py-1 text-sm font-mono-data"
                        value={delta}
                        onChange={(e) => setDelta(e.target.value)}
                        data-testid="plan-extend-delta"
                      />
                      <span className="text-xs text-gray-600">€/mes</span>
                      <span className="text-xs font-medium text-amber-800">
                        = {fmtEur(Number(plan.monthly_amount) + Number(delta || 0))}/mes
                      </span>
                    </div>
                  )}
                </div>
              </label>
            </RadioGroup>
          </div>

          {previewErr && (
            <p className="text-xs text-red-600">{previewErr}</p>
          )}
          {preview && (
            <div className="p-2 rounded bg-amber-50/50 border border-amber-200 text-xs space-y-1">
              <p className="text-amber-900 font-medium">Vista previa</p>
              <p>
                Total del plan tras ampliar:{" "}
                <span className="font-mono-data font-semibold">{fmtEur(preview.total_amount)}</span> ·
                Cuota mensual: <span className="font-mono-data font-semibold">{fmtEur(preview.monthly_amount)}</span>
              </p>
              <p>Plazos totales: <span className="font-mono-data">{preview.installments.length}</span> (pagados{" "}
                <span className="font-mono-data">{preview.installments.filter((i) => i.status === "paid").length}</span>)
              </p>
              <div className="max-h-32 overflow-y-auto pt-1">
                {preview.installments.map((i) => (
                  <div key={i.n} className="flex justify-between text-[11px] py-0.5">
                    <span className="text-gray-600">
                      Plazo {i.n} · vence {fmtDate(i.due_date)}
                      {i.status === "paid" && " · pagado"}
                    </span>
                    <span className="font-mono-data">{fmtEur(i.amount_eur)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={saving || !preview || selected.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="plan-extend-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Ampliar plan
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
